import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "antd";

const MIN_ZOOM = 100;
const MAX_ZOOM = 500;
const ZOOM_STEP = 25;

const CalibrationZoomStep = ({
    onNext,
    onBack,
    onClose,
    imgSrc,
    viewState,
    setViewState,
}) => {
    const previewRef = useRef(null);
    const imageRef = useRef(null);
    const dragStartRef = useRef({
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0,
    });

    const [dragging, setDragging] = useState(false);
    const [isImageReady, setIsImageReady] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const { zoom, offset } = viewState;

    useEffect(() => {
        const handleWindowMouseUp = () => {
            setDragging(false);
        };

        window.addEventListener("mouseup", handleWindowMouseUp);
        return () => window.removeEventListener("mouseup", handleWindowMouseUp);
    }, []);

    useEffect(() => {
        const img = imageRef.current;
        const preview = previewRef.current;

        if (!img || !preview || !isImageReady || initialized) return;

        const previewWidth = preview.clientWidth;
        const previewHeight = preview.clientHeight;
        const imageWidth = img.naturalWidth || img.width;
        const imageHeight = img.naturalHeight || img.height;

        const centeredX = (previewWidth - imageWidth) / 2;
        const centeredY = (previewHeight - imageHeight) / 2;

        setViewState((prev) => {
            if (prev.offset.x !== 0 || prev.offset.y !== 0) {
                return prev;
            }

            return {
                ...prev,
                offset: {
                    x: centeredX,
                    y: centeredY,
                },
            };
        });

        setInitialized(true);
    }, [isImageReady, initialized, setViewState]);

    const transformStyle = useMemo(() => {
        return {
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom / 100})`,
            transformOrigin: "top left",
        };
    }, [offset, zoom]);

    const updateZoom = (direction) => {
        setViewState((prev) => ({
            ...prev,
            zoom:
                direction === "in"
                    ? Math.min(prev.zoom + ZOOM_STEP, MAX_ZOOM)
                    : Math.max(prev.zoom - ZOOM_STEP, MIN_ZOOM),
        }));
    };

    const handleMouseDown = (event) => {
        event.preventDefault();

        dragStartRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: offset.x,
            originY: offset.y,
        };

        setDragging(true);
    };

    const handleMouseMove = (event) => {
        if (!dragging) return;

        const deltaX = event.clientX - dragStartRef.current.startX;
        const deltaY = event.clientY - dragStartRef.current.startY;

        setViewState((prev) => ({
            ...prev,
            offset: {
                x: dragStartRef.current.originX + deltaX,
                y: dragStartRef.current.originY + deltaY,
            },
        }));
    };

    const handleMouseUp = () => {
        setDragging(false);
    };

    const handleMouseLeave = () => {
        setDragging(false);
    };

    return (
        <div className="calibration-zoom-step">
            <button
                type="button"
                className="calibration-zoom-step__close"
                onClick={onClose}
            >
                ✕
            </button>

            <div className="calibration-zoom-step__wrapper">
                <div className="calibration-zoom-step__header">
                    <div className="calibration-zoom-step__badge">1</div>

                    <div className="calibration-zoom-step__title-group">
                        <h1>Ubica una fila y ajusta el zoom</h1>
                        <p>
                            Encuentra una fila larga dentro de la imagen. Puedes arrastrar para moverla
                            y usar los controles para acercar o alejar hasta verla con mayor precisión.
                        </p>
                    </div>
                </div>

                <div className="calibration-zoom-step__preview-card">
                    <div
                        ref={previewRef}
                        className={`calibration-zoom-step__preview ${dragging ? "is-dragging" : ""}`}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                    >
                        <div
                            className="calibration-zoom-step__image-layer"
                            style={transformStyle}
                        >
                            <img
                                ref={imageRef}
                                src={imgSrc}
                                alt="Reference chart"
                                draggable={false}
                                onLoad={() => setIsImageReady(true)}
                            />
                        </div>
                    </div>
                </div>

                <div className="calibration-zoom-step__zoom-box">
                    <span>Nivel de zoom</span>

                    <div className="calibration-zoom-step__zoom-control">
                        <button
                            type="button"
                            onClick={() => updateZoom("out")}
                            disabled={zoom <= MIN_ZOOM}
                        >
                            −
                        </button>

                        <span>{zoom} %</span>

                        <button
                            type="button"
                            onClick={() => updateZoom("in")}
                            disabled={zoom >= MAX_ZOOM}
                        >
                            +
                        </button>
                    </div>
                </div>

                <div className="calibration-zoom-step__actions">
                    <Button type="link" size="large" onClick={onBack}>
                        Atras
                    </Button>

                    <Button type="link" size="large" onClick={onNext}>
                        Siguiente
                    </Button>
                </div>
            </div>
        </div>
    );
};

export { CalibrationZoomStep };